import moment from "moment";
import { API_DATE } from "../Shared/formatters";
import { hhmmToHours } from "../Shared/importMapping";
import { LEAVE_TYPES } from "./leaveEarningsData";

// Column mapping + checks for Pay Inputs > Leave > Import. Same matching
// rules as the Overtime import (Overtime/overtimeData.js): a column is
// matched by its header against the field label + aliases, then by its
// values. Rows that pass are posted to the Leave import API, which does the
// final checks (employee, payroll cycle, duplicates, payroll lock).

export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_ACCEPT = ".xlsx,.xls,.csv";

export const IMPORT_FIELDS = [
  {
    key: "employee_code",
    label: "Employee ID",
    required: true,
    aliases: ["employee id", "employee code", "emp id", "emp code", "emp no", "employee no", "employee number", "staff id", "staff code"],
  },
  {
    key: "date",
    label: "Date",
    required: true,
    aliases: ["date", "leave date", "attendance date", "day", "dt"],
  },
  {
    key: "earning_type",
    label: "Leave Type",
    required: false,
    aliases: ["leave type", "earning type", "day type", "type", "paid leave"],
  },
  {
    key: "basis",
    label: "Basis",
    required: false,
    aliases: ["basis", "pay basis", "calculation basis", "unit"],
  },
  {
    key: "quantity",
    label: "Credited Qty",
    required: true,
    aliases: ["credited qty", "credited quantity", "credit qty", "credited", "quantity", "qty", "leave days", "days"],
  },
  {
    key: "rate",
    label: "Rate (per unit)",
    required: true,
    aliases: ["rate", "rate per unit", "pay rate", "unit rate", "per day", "per day salary", "rate per day"],
  },
  {
    key: "payment_factor",
    label: "Factor",
    required: false,
    aliases: ["factor", "payment factor", "pay factor", "multiplier"],
  },
  // No Amount column: the server calculates it (same as Shift Earnings and
  // Overtime). The validate API's amount is shown in the preview.
  {
    key: "shift_name",
    label: "Shift",
    required: false,
    aliases: ["shift", "shift name"],
  },
];

// The Leave Type line lists the leave types from the server
// (payroll/earning/leave/earning/types/), or LEAVE_TYPES if they can't load.
export const importGuidelines = (types = LEAVE_TYPES) => [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Employee ID is the employee code (e.g. VST1005).",
  "Date format: DD MMM YYYY (e.g. 15 Oct 2026), DD-MM-YYYY or YYYY-MM-DD.",
  `Leave Type is one of ${types.join(", ")} (blank = ${defaultLeaveType(types)}). Basis is Shift or Hours (blank = Shift).`,
  "Credited Qty, Rate and Factor are numbers (Factor blank = 1). Hours basis takes decimal hours (4.5).",
  "Amount is calculated by the system: Credited Qty × Rate × Factor.",
  "Rows go into the payroll cycle chosen on this step, or each row's own cycle when left as Automatic.",
  "One row per employee, date and leave type. Maximum file size: 5 MB.",
];

const BASIS_VALUES = { shift: "shift", shifts: "shift", day: "shift", days: "shift", hours: "hours", hour: "hours", hrs: "hours", hourly: "hours" };

const normalize = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const words = (value) =>
  String(value ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

// Dice coefficient on letter pairs: 1 = identical, 0 = nothing shared.
const pairSimilarity = (a, b) => {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const pairs = (text) => {
    const list = [];
    for (let i = 0; i < text.length - 1; i += 1) list.push(text.slice(i, i + 2));
    return list;
  };
  const left = pairs(a);
  const pool = pairs(b);
  const total = left.length + pool.length;
  let shared = 0;
  left.forEach((pair) => {
    const at = pool.indexOf(pair);
    if (at !== -1) {
      shared += 1;
      pool.splice(at, 1);
    }
  });
  return (2 * shared) / total;
};

// How well a header names a field, 0..1.
const headerScore = (header, field) => {
  const name = normalize(header);
  if (!name) return 0;
  const headerWords = words(header);
  let best = 0;
  [field.label, ...field.aliases].forEach((candidate) => {
    const target = normalize(candidate);
    if (name === target) {
      best = Math.max(best, 1);
      return;
    }
    const targetWords = words(candidate);
    if (targetWords.length && targetWords.every((word) => headerWords.includes(word))) {
      best = Math.max(best, 0.9 - 0.02 * (headerWords.length - targetWords.length));
    }
    best = Math.max(best, pairSimilarity(name, target) * 0.85);
  });
  return best;
};

const HEADER_MATCH_MIN = 0.6;
const CONTENT_MATCH_MIN = 0.6;

const isBlankCell = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const DATE_FORMATS = ["DD MMM YYYY", "D MMM YYYY", API_DATE, "DD-MM-YYYY", "DD/MM/YYYY"];

export const parseDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format(API_DATE);
  }
  const parsed = moment(String(value ?? "").trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(API_DATE) : null;
};

// `types`: the leave types from the server, or LEAVE_TYPES.
export const matchLeaveType = (value, types = LEAVE_TYPES) =>
  types.find((type) => normalize(type) === normalize(value)) ?? null;

// A blank Leave Type cell means "Leave" when that type exists, else the
// first type in the list.
export const defaultLeaveType = (types = LEAVE_TYPES) => (types.includes("Leave") ? "Leave" : types[0] ?? "Leave");

export const matchBasis = (value) => BASIS_VALUES[normalize(value)] ?? null;

// Fields that can be recognised from their values alone.
const contentRules = (types) => ({
  date: (value) => Boolean(parseDate(value)),
  earning_type: (value) => Boolean(matchLeaveType(value, types)),
  basis: (value) => Boolean(matchBasis(value)),
});

const contentScore = (rows, index, test) => {
  const sample = rows
    .map(({ cells }) => cells[index])
    .filter((value) => !isBlankCell(value))
    .slice(0, 50);
  if (!sample.length) return 0;
  return sample.filter((value) => test(value)).length / sample.length;
};

// { [columnIndex]: fieldKey }. Each field and each column is used at most
// once; the strongest header matches win first, then remaining fields are
// filled from column contents.
export const autoMapColumns = (headers, rows = [], types = LEAVE_TYPES) => {
  const CONTENT_RULES = contentRules(types);
  const mapping = {};
  const usedFields = new Set();
  const usedColumns = new Set();

  const assign = (candidates, minimum) => {
    candidates
      .filter((candidate) => candidate.score >= minimum)
      .sort((a, b) => b.score - a.score)
      .forEach(({ index, key }) => {
        if (usedFields.has(key) || usedColumns.has(index)) return;
        mapping[index] = key;
        usedFields.add(key);
        usedColumns.add(index);
      });
  };

  const byHeader = [];
  headers.forEach((header, index) => {
    IMPORT_FIELDS.forEach((field) => {
      byHeader.push({ index, key: field.key, score: headerScore(header, field) });
    });
  });
  assign(byHeader, HEADER_MATCH_MIN);

  const byContent = [];
  headers.forEach((header, index) => {
    if (usedColumns.has(index)) return;
    Object.entries(CONTENT_RULES).forEach(([key, test]) => {
      if (usedFields.has(key)) return;
      byContent.push({ index, key, score: contentScore(rows, index, test) });
    });
  });
  assign(byContent, CONTENT_MATCH_MIN);

  return mapping;
};

export const missingRequiredFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

// First non-empty row is the header row; fully blank rows are skipped.
// Data rows are numbered 1, 2, 3... (header and blank rows not counted).
export const splitSheetRows = (sheetRows) => {
  const isBlank = (row) => !row || row.every(isBlankCell);
  const headerIndex = sheetRows.findIndex((row) => !isBlank(row));
  if (headerIndex === -1) return { headers: [], rows: [] };
  const headers = sheetRows[headerIndex].map((cell) => String(cell ?? "").trim());
  const rows = [];
  sheetRows.slice(headerIndex + 1).forEach((cells) => {
    if (!isBlank(cells)) rows.push({ rowNumber: rows.length + 1, cells });
  });
  return { headers, rows };
};

const toNumber = (value) =>
  isBlankCell(value) ? NaN : Number(String(value).replace(/,/g, "").trim());

// Same employee + date + leave type key the duplicate check uses.
export const duplicateKey = (employeeCode, date, earningType) => `${String(employeeCode).toLowerCase()}|${date}|${earningType}`;

// Checks every data row against the mapping. Returns
// { rowNumber, raw: { [fieldKey]: raw }, errors: [string], row } where `row`
// is the API record (plus payable_amount for the preview) for valid rows.
// A later row with the same employee, date and leave type as an earlier one
// is a duplicate.
export const validateImportRows = (rows, mapping, types = LEAVE_TYPES) => {
  const seen = new Set();
  return rows.map(({ rowNumber, cells }) => {
    const raw = {};
    Object.entries(mapping).forEach(([index, key]) => {
      raw[key] = cells[Number(index)];
    });
    return checkImportRow(rowNumber, raw, types, seen);
  });
};

// Checks one row's values ({ [fieldKey]: value }). Also used to re-check a
// row corrected in the preview's Edit panel. `seen` (optional): duplicate
// keys of the rows before it; this row's key is added to it.
export const checkImportRow = (rowNumber, raw, types = LEAVE_TYPES, seen = null) => {
  const errors = [];
  const label = (key) => IMPORT_FIELDS.find((field) => field.key === key).label;

  const employeeCode = String(raw.employee_code ?? "").trim();
  if (!employeeCode) errors.push("Employee ID is required");

  let date = null;
  if (isBlankCell(raw.date)) errors.push("Date is required");
  else {
    date = parseDate(raw.date);
    if (!date) errors.push("Invalid date format");
  }

  let earningType = defaultLeaveType(types);
  if (!isBlankCell(raw.earning_type)) {
    earningType = matchLeaveType(raw.earning_type, types);
    if (!earningType) errors.push(`Leave Type "${raw.earning_type}" is not valid`);
  }

  let basis = "shift";
  if (!isBlankCell(raw.basis)) {
    basis = matchBasis(raw.basis);
    if (!basis) errors.push(`Basis "${raw.basis}" is not valid`);
  }

  const numeric = (key, { required = true } = {}) => {
    if (isBlankCell(raw[key])) {
      if (required) errors.push(`${label(key)} is required`);
      return null;
    }
    // Hours basis: Credited Qty may be HH:MM (02:30 -> 2.5 hours).
    const hoursQty = key === "quantity" && basis === "hours";
    const number = (hoursQty ? hhmmToHours(raw[key]) : null) ?? toNumber(raw[key]);
    if (!Number.isFinite(number)) {
      errors.push(`${label(key)} must be ${hoursQty ? "HH:MM or a number" : "a numeric value"}`);
      return null;
    }
    if (number < 0) errors.push(`${label(key)} cannot be negative`);
    return number;
  };
  const quantity = numeric("quantity");
  const rate = numeric("rate");
  const factor = numeric("payment_factor", { required: false }) ?? 1;

  if (seen && employeeCode && date && earningType) {
    const key = duplicateKey(employeeCode, date, earningType);
    if (seen.has(key)) errors.push("Duplicate row in the file for this employee, date and leave type");
    seen.add(key);
  }

  const shiftName = String(raw.shift_name ?? "").trim();
  return {
    rowNumber,
    raw,
    errors,
    row: errors.length
      ? null
      : {
          employee_id: employeeCode,
          date,
          earning_type: earningType,
          basis,
          quantity,
          rate,
          factor,
          ...(shiftName ? { shift: shiftName } : {}),
          // Browser estimate until the validate API answers; never sent.
          payable_amount: quantity * rate * factor,
        },
  };
};

// Template rows (header + two examples) for the downloadable .xlsx.
export const importTemplateRows = () => [
  IMPORT_FIELDS.map((field) => field.label),
  ["VST1005", moment().startOf("month").format("DD MMM YYYY"), "Paid Leave", "Shift", 1, 800, 1, "General SHIFT"],
  ["VST1031", moment().startOf("month").add(1, "day").format("DD MMM YYYY"), "Paid Leave", "Shift", 0.5, 800, "", ""],
];

// Merges the server check (validate API) into the file check; see
// Shared/importMapping.js.
export { applyServerChecks } from "../Shared/importMapping";
